import React, { useState } from 'react';
import { Send, Mail, User, Phone, MessageSquare, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { trackEnquirySubmit, trackCalendlyClick, trackBookSessionClick } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    studentTier: 'higher',
    pricingOption: 'monthly',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [enquiryId, setEnquiryId] = useState(null);
  const [showPaymentOption, setShowPaymentOption] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(9999); // Default to monthly

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Update amount based on pricing option
    if (name === 'pricingOption') {
      if (value === 'oneoff') {
        setSelectedAmount(2999);
      } else if (value === 'vip') {
        setSelectedAmount(6500);
      } else {
        setSelectedAmount(9999);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Call backend API
      const response = await axios.post(`${API}/enquiries/`, {
        student_name: formData.name,
        email: formData.email,
        phone: formData.phone,
        student_tier: formData.studentTier,
        message: formData.message
      });

      if (response.data.success) {
        const newEnquiryId = response.data.enquiry_id;
        setEnquiryId(newEnquiryId);
        setShowPaymentOption(true);
        
        // Track enquiry submission in GA4
        trackEnquirySubmit(formData.pricingOption, formData.studentTier);
        
        toast({
          title: "Enquiry Received!",
          description: response.data.message || "Thank you for your interest. You can now proceed to book and pay for your session.",
        });
      }
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      toast({
        title: "Submission Failed",
        description: error.response?.data?.detail || "There was an error submitting your enquiry. Please try again or contact us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProceedToPayment = () => {
    if (enquiryId) {
      // Track payment button click
      trackBookSessionClick('contact_form');
      navigate(`/payment?enquiryId=${enquiryId}&amount=${selectedAmount}&option=${formData.pricingOption}`);
    }
  };

  const handleResetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      studentTier: 'higher',
      pricingOption: 'monthly',
      message: ''
    });
    setShowPaymentOption(false);
    setEnquiryId(null);
    setSelectedAmount(9999);
  };

  return (
    <section id="contact" style={{ padding: '5rem 1.5rem', background: 'var(--bg-section)' }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
            Get Started Today
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
            Fill out the form below and we'll get back to you within 24 hours to discuss your tuition needs
          </p>
        </div>

        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <div className="product-card">
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {/* Name Field */}
              <div>
                <label htmlFor="name" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                  Student Name *
                </label>
                <div style={{ position: 'relative' }}>
                  <User size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter student's full name"
                    style={{ paddingLeft: '40px' }}
                  />
                </div>
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                  Email Address *
                </label>
                <div style={{ position: 'relative' }}>
                  <Mail size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your.email@example.com"
                    style={{ paddingLeft: '40px' }}
                  />
                </div>
              </div>

              {/* Phone Field */}
              <div>
                <label htmlFor="phone" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                  Phone Number *
                </label>
                <div style={{ position: 'relative' }}>
                  <Phone size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="07123 456789"
                    style={{ paddingLeft: '40px' }}
                  />
                </div>
              </div>

              {/* Tier Selection */}
              <div>
                <label htmlFor="studentTier" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                  Which tier are you interested in? *
                </label>
                <select
                  id="studentTier"
                  name="studentTier"
                  required
                  value={formData.studentTier}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '0.625rem 0.75rem',
                    border: '1px solid var(--border-light)',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    color: 'var(--text-body)',
                    background: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="higher">Higher Tier (Grades 4-9)</option>
                  <option value="foundation">Foundation Tier (Grades 1-5)</option>
                  <option value="unsure">Not Sure - Need Advice</option>
                </select>
              </div>

              {/* Pricing Option Selection */}
              <div>
                <label htmlFor="pricingOption" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                  Select Your Pricing Option *
                </label>
                <select
                  id="pricingOption"
                  name="pricingOption"
                  required
                  value={formData.pricingOption}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '0.625rem 0.75rem',
                    border: '1px solid var(--border-light)',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    color: 'var(--text-body)',
                    background: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="monthly">Monthly Package - £119.99/month (4 lessons - Best Value!)</option>
                  <option value="vip">VIP 1-on-1 - £65/hour (Premium Private Tuition)</option>
                  <option value="oneoff">One-Off Lesson - £34.99/lesson (Group Session)</option>
                </select>
              </div>

              {/* Message Field */}
              <div>
                <label htmlFor="message" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                  Additional Information
                </label>
                <div style={{ position: 'relative' }}>
                  <MessageSquare size={18} style={{ position: 'absolute', left: '12px', top: '12px', color: 'var(--text-muted)' }} />
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your current grade, target grade, exam date, and any specific areas you need help with..."
                    rows={4}
                    style={{ paddingLeft: '40px' }}
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="btn-primary" 
                disabled={isSubmitting || showPaymentOption}
                style={{ width: '100%', gap: '0.5rem' }}
              >
                {isSubmitting ? 'Sending...' : 'Send Enquiry'}
                {!isSubmitting && <Send size={18} />}
              </Button>

              <p className="caption" style={{ textAlign: 'center', marginTop: '0.5rem' }}>
                By submitting this form, you agree to be contacted about our tuition services.
              </p>
            </form>

            {/* Payment Option After Submission */}
            {showPaymentOption && (
              <div style={{
                marginTop: '2rem',
                padding: '1.5rem',
                background: 'var(--accent-wash)',
                borderRadius: '12px',
                border: '2px solid var(--accent-primary)'
              }}>
                <h3 className="heading-3" style={{ marginBottom: '1rem', textAlign: 'center' }}>
                  Ready to Book Your Session?
                </h3>
                <p className="body-medium" style={{ textAlign: 'center', marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
                  {formData.pricingOption === 'monthly' 
                    ? 'Book your monthly package now (4 lessons per month)'
                    : formData.pricingOption === 'vip'
                    ? 'Book your premium VIP 1-on-1 session'
                    : 'Book your one-off lesson now'}
                </p>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                  <Button 
                    onClick={handleProceedToPayment}
                    className="btn-primary"
                  >
                    Book & Pay £{(selectedAmount / 100).toFixed(2)} Now
                  </Button>
                  <Button 
                    onClick={handleResetForm}
                    className="btn-secondary"
                  >
                    Submit Another Enquiry
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div style={{
            marginTop: '2rem',
            textAlign: 'center',
            padding: '1.5rem',
            background: 'var(--bg-card)',
            border: '1px solid var(--border-light)',
            borderRadius: '12px'
          }}>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              Or reach out directly:
            </p>
            <p className="body-medium" style={{ color: 'var(--accent-text)', fontWeight: 600, marginBottom: '0.25rem' }}>
              Email: drmomakobia@abbaacademy.com
            </p>
            <p className="body-medium" style={{ color: 'var(--accent-text)', fontWeight: 600 }}>
              Phone: +44 7948 213565
            </p>
          </div>

          {/* Calendly CTA */}
          <div style={{
            marginTop: '2rem',
            padding: '2rem',
            background: 'linear-gradient(135deg, var(--accent-wash) 0%, rgba(64, 224, 208, 0.15) 100%)',
            border: '2px solid var(--accent-primary)',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: 'var(--accent-primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem'
            }}>
              <Calendar size={28} color="white" />
            </div>
            <h3 className="heading-3" style={{ marginBottom: '1rem' }}>
              Prefer to Talk First?
            </h3>
            <p className="body-medium" style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
              Schedule a free consultation call with Dr. Michael to discuss your child's needs, goals, and how we can help them excel in GCSE Maths.
            </p>
            <a 
              href="https://calendly.com/drmomakobia/studentcall"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{ textDecoration: 'none', display: 'inline-flex', gap: '0.5rem' }}
              onClick={() => trackCalendlyClick('contact_section')}
            >
              <Calendar size={20} />
              Schedule Free Consultation
            </a>
          </div>

          {/* Referral Incentive */}
          <div style={{
            marginTop: '2rem',
            padding: '1.5rem',
            background: 'linear-gradient(135deg, rgba(255, 213, 79, 0.1) 0%, var(--accent-wash) 100%)',
            border: '2px solid var(--accent-secondary)',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <p className="body-medium" style={{ fontWeight: 600, color: 'var(--accent-secondary-text)', marginBottom: '0.5rem' }}>
              💡 Pro Tip: Bring Your Friends!
            </p>
            <p className="body-small" style={{ color: 'var(--text-body)', margin: 0 }}>
              Sign up with your friendship group and earn a FREE session for every friend you refer to Abba Academy. Smash your maths goals together!
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
